import { ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react'

import { t } from 'lib/i18n'

import { Button, Modal } from './ui'

interface ConfirmOptions {
  title?: string
  confirmLabel?: string
  cancelLabel?: string
  tone?: 'danger' | 'primary'
}

type Confirm = (message: string, options?: ConfirmOptions) => Promise<boolean>

const ConfirmContext = createContext<Confirm>(() => Promise.resolve(false))

interface ConfirmState extends ConfirmOptions {
  message: string
  resolve: (value: boolean) => void
}

/** In-app replacement for window.confirm — same call shape (await, returns boolean) but renders our own Modal
 *  instead of the browser's native dialog, which looks inconsistent across platforms and can freeze automated
 *  browser control. */
export const ConfirmProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<ConfirmState | null>(null)

  const confirm = useCallback<Confirm>(
    (message, options) => new Promise<boolean>((resolve) => setState({ ...options, message, resolve })),
    []
  )

  const settle = (value: boolean) => {
    state?.resolve(value)
    setState(null)
  }

  const value = useMemo(() => confirm, [confirm])

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      {state ? (
        <Modal
          title={state.title ?? t('تأكيد', 'Confirm')}
          onClose={() => settle(false)}
          footer={
            <>
              <Button onClick={() => settle(false)}>{state.cancelLabel ?? t('إلغاء', 'Cancel')}</Button>
              <Button variant={state.tone === 'primary' ? 'primary' : 'danger'} onClick={() => settle(true)}>
                {state.confirmLabel ?? t('تأكيد', 'Confirm')}
              </Button>
            </>
          }
        >
          <p>{state.message}</p>
        </Modal>
      ) : null}
    </ConfirmContext.Provider>
  )
}

export const useConfirm = () => useContext(ConfirmContext)
